import {
    type KeyboardEvent,
    type ReactNode,
    type Ref,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import classNames from 'classnames';

import TextField from '@/components/atoms/TextField';

import styles from './Combobox.module.scss';

export interface ComboboxProps<T> {
    /** What the field holds; the caller owns it, so it can search with it. */
    query: string;
    onQueryChange: (query: string) => void;
    options: readonly T[];
    getKey: (option: T) => string;
    renderOption: (option: T) => ReactNode;
    onSelect: (option: T) => void;
    /**
     * Said in place of the list and announced as it changes, such as "Searching" or
     * "No matches"; empty while the options speak for themselves.
     */
    status?: string;
    /**
     * `popup` opens a list under the field while there is something to show; `inline` keeps
     * the list in the flow, for a picker that already sits in a panel of its own.
     */
    variant?: 'popup' | 'inline';
    /** Escape when the list is closed, or always in the inline variant, e.g. to close a panel. */
    onEscape?: () => void;
    id?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    placeholder?: string;
    maxLength?: number;
    /** Off for codes and ids, which the browser would mark as misspelt. */
    spellCheck?: boolean;
    autoFocus?: boolean;
    inputRef?: Ref<HTMLInputElement>;
    className?: string;
}

/**
 * A field that narrows a list as it is typed into and picks one option from it: the arrows
 * move a highlight that wraps at the ends, Enter picks it, Escape closes the list. The field
 * keeps focus throughout, and a screen reader hears the highlighted option and the status.
 */
export default function Combobox<T>(props: ComboboxProps<T>) {
    const {
        query,
        onQueryChange,
        options,
        getKey,
        renderOption,
        onSelect,
        status = '',
        variant = 'popup',
        onEscape,
        placeholder,
        maxLength,
        spellCheck,
        autoFocus = false,
        inputRef,
        className,
    } = props;
    // React's ids hold colons, which jsdom's selector engine cannot match in tests.
    const baseId = useId().replaceAll(':', '');
    const listId = `${baseId}-list`;
    const [active, setActive] = useState(-1);
    const [popupOpen, setPopupOpen] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    const listShown = variant === 'inline' || popupOpen;
    const hasOptions = options.length > 0;
    const optionId = (index: number) => `${baseId}-option-${index}`;

    // A new list starts without a highlight, so Enter never picks what the reader has not seen.
    const [shownOptions, setShownOptions] = useState(options);
    if (shownOptions !== options) {
        setShownOptions(options);
        setActive(-1);
    }

    useEffect(() => {
        if (active < 0) return;
        listRef.current
            ?.querySelector(`#${optionId(active)}`)
            ?.scrollIntoView?.({ block: 'nearest' });
    });

    const pick = (option: T) => {
        onSelect(option);
        setPopupOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        // Keys typed here must not reach a menu around the field. Escape is the exception: one
        // the field has no use for goes on, so the dialog or menu around it can close.
        if (event.key !== 'Escape') event.stopPropagation();
        switch (event.key) {
            case 'ArrowDown':
            case 'ArrowUp': {
                event.preventDefault();
                setPopupOpen(true);
                if (!hasOptions) return;
                const step = event.key === 'ArrowDown' ? 1 : -1;
                setActive((current) =>
                    current < 0
                        ? step > 0
                            ? 0
                            : options.length - 1
                        : (current + step + options.length) % options.length
                );
                return;
            }
            case 'Enter': {
                // A pick, never a form submit.
                event.preventDefault();
                const option = options[active];
                if (listShown && option !== undefined) pick(option);
                return;
            }
            case 'Escape':
                if (variant === 'popup' && popupOpen) {
                    event.preventDefault();
                    event.stopPropagation();
                    setPopupOpen(false);
                    return;
                }
                if (onEscape) {
                    event.preventDefault();
                    event.stopPropagation();
                    onEscape();
                }
                return;
            default:
                return;
        }
    };

    return (
        <div className={classNames(styles.combobox, styles[variant], className)}>
            <TextField
                ref={inputRef}
                id={props.id}
                type="text"
                role="combobox"
                autoComplete="off"
                aria-autocomplete="list"
                aria-expanded={listShown && hasOptions}
                aria-controls={listId}
                aria-activedescendant={listShown && active >= 0 ? optionId(active) : undefined}
                aria-label={props['aria-label']}
                aria-labelledby={props['aria-labelledby']}
                placeholder={placeholder}
                maxLength={maxLength}
                spellCheck={spellCheck}
                autoFocus={autoFocus}
                value={query}
                className={styles.field}
                onChange={(event) => {
                    // The highlight belonged to the old text; Enter must not pick it.
                    setActive(-1);
                    onQueryChange(event.target.value);
                    setPopupOpen(true);
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setPopupOpen(false)}
            />
            {/* Stays mounted, so a screen reader hears each change of the status. */}
            <output className={classNames(styles.status, !listShown && styles.hidden)}>
                {listShown ? status : ''}
            </output>
            <div
                ref={listRef}
                id={listId}
                role="listbox"
                aria-label={props['aria-label']}
                aria-labelledby={props['aria-labelledby']}
                hidden={!listShown || !hasOptions}
                className={styles.list}>
                {options.map((option, index) => (
                    <div
                        key={getKey(option)}
                        id={optionId(index)}
                        role="option"
                        // Reachable by script, never by Tab: the field keeps focus and points
                        // at the highlighted option.
                        tabIndex={-1}
                        aria-selected={index === active}
                        className={classNames(styles.option, index === active && styles.active)}
                        // Keeps focus in the field, so the click lands as a pick.
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseMove={() => setActive(index)}
                        onClick={() => pick(option)}>
                        {renderOption(option)}
                    </div>
                ))}
            </div>
        </div>
    );
}

export { Combobox };
